import { Languages } from '@beable/content-types';
import {
    AcademicYearDTO,
    ActivityQuestionScore,
    BeableCourseType,
    CloneCourseScheduleRequest,
    CourseAvailabilityPayload,
    CourseAvailabilityType,
    CourseDeliveryDTO,
    CourseFrequencyPayload,
    CourseFrequencyType,
    CourseSetDTO,
    DeprovisionSiteCourseRequest,
    LessonHistoryDataRequest,
    LessonHistoryFilterTypes,
    LessonStatusFilter,
    ModifyActivityQuestionScoresRequest,
    ProvisionSiteCourseRequest,
    ReportDates,
    SelectionsResponse,
    SiteCourseScheduleRequest,
    SiteCourseSettingsRequest,
    SiteDTO,
    UserAccountDTO,
    UserRole,
    UserScheduleRequest
} from '@beable/roster-backend-client-v1';
import { format, toZonedTime } from 'date-fns-tz';
import RosterBackendService from './RosterBackendService';
 
export interface DeliveryRules {
    frequencyType?: CourseFrequencyType;
    frequencyPayload?: CourseFrequencyPayload;
    availabilityType?: CourseAvailabilityType;
    availabilityPayload?: CourseAvailabilityPayload;
    oldStart?: Date;
    languageType?: Languages;
    ignoreSkipDates?: boolean;
}

async function getContentCourses(locationCode: string) {
    const response = await RosterBackendService.getCourseList(locationCode);
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getCoursesForLocation(locationCode?: string) {
    const response = await RosterBackendService.getCourseDeliveryForLocation({ locationCode });
    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getProvisionedContentCourses(siteId: number, orgId: string, academicYearId: string) {
    const response = await RosterBackendService.getProvisionedContentCourses({ siteId, orgId, academicYearId })

    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getCourseSets(locationCode?: string, siteId?: number) {
    const response = await RosterBackendService.getCourseSetsByLocation({ locationCode, siteId })

    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function getCourseSet(courseSetId: string) {
    const response = await RosterBackendService.getCourseSet(courseSetId)

    if (response.isOk()) {
        return response.value;
    }

    return null;
}

async function saveCourseSet(courseSet: CourseSetDTO) {
    const response = await RosterBackendService.saveCourseSet(courseSet)

    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function getCourseAccessRules(siteId: number, courseId?: string, courseSetId?: string, orgId?: string): Promise<SelectionsResponse | string[] | undefined> { // TODO: remove string[]. Added for backwards compatability
    const request: SiteCourseSettingsRequest = { siteId, courseId, courseSetId, orgId }
    const response = await RosterBackendService.getCourseAccessRules(request);
    if (response.isOk()) {
        return response.value;
    }

    return undefined;
}

async function getCourseSetStatusSummaryForSite(siteId: number, academicYearId: string, courseId?: string, courseSetId?: string) {
    const response = await RosterBackendService.getCourseSetStatusSummaryForSite({
        siteId: siteId,
        academicYearId: academicYearId,
        courseId: courseId,
        courseSetId: courseSetId
    });
    
    if (response.isOk()) {
        return response.value;
    }

    return undefined;
}

async function provisionSiteCourse(
    site: SiteDTO, course: CourseDeliveryDTO | undefined, courseSet: CourseSetDTO | undefined,
    academicYears: AcademicYearDTO[], orgId?: string, accessSelections?: string, deliveryRules?: DeliveryRules,
    preview: boolean = false, urgent: boolean = false
)  {
    const request: ProvisionSiteCourseRequest =
    {
        siteId: site.id,
        course: course,
        courseSet: courseSet,
        orgId,
        academicYears: academicYears.map((academicYear) => {
            return {
              id: academicYear.academicYearId,
              startDate: academicYear.startAt,
              endDate: academicYear.endAt,
            }
        }),
        timezone: site.timezone ?? '',
        delivery: deliveryRules && {
            frequencyType: deliveryRules.frequencyType ?? '' as CourseFrequencyType,
            frequencyPayload: deliveryRules.frequencyPayload ?? {} as CourseFrequencyPayload,
            availabilityType: deliveryRules.availabilityType ?? '' as CourseAvailabilityType,
            availabilityPayload: deliveryRules.availabilityPayload ?? {} as CourseAvailabilityPayload,
            languageCode: deliveryRules.languageType ?? null as unknown as Languages,
            ignoreSkipDates: deliveryRules.ignoreSkipDates ?? false
        },
        urgent
    }

    if (accessSelections) {
        request.access = { selections: accessSelections }
    }

    let response: any;
    if (preview) {
        response = await RosterBackendService.previewProvisionedSchedule(request);
    } else {
        response = await RosterBackendService.provisionSiteCourse(request);
    }

    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function cloneSchedule(
    siteId: number, cloneFromOrgId: string, copyToOrgId: string, courseSets: CourseSetDTO[], academicYear: AcademicYearDTO, timezone: string
): Promise<boolean> {
    const request: CloneCourseScheduleRequest = {
        siteId, 
        cloneFromOrgId,
        copyToOrgId,
        courseSets,
        academicYear: {
            id: academicYear.academicYearId,
            startDate: academicYear.startAt,
            endDate: academicYear.endAt
        },
        timezone
    }
    const response = await RosterBackendService.cloneSchedule(request);
    if (response.isOk()) {
        return true;
    }
    return false;
}

async function deprovisionSiteCourse(
    siteId: number, courseSetId: string | undefined, courseId: string | undefined, academicYearIds: string[], orgId?: string
) {
    const request: DeprovisionSiteCourseRequest = { siteId, courseId, courseSetId, academicYearIds, orgId }
    const response = await RosterBackendService.deprovisionSiteCourse(request);

    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function getCourseDeliveryRules(siteId: number, courseId?: string, courseSetId?: string, orgId?: string) {
    const request: SiteCourseSettingsRequest = { siteId, courseId, courseSetId, orgId }
    const response = await RosterBackendService.getCourseDeliveryRules(request);
    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function getCourseScheduleData(siteId: number, courseSetId: string, orgId: string, academicYearId: string) {
    const request: SiteCourseScheduleRequest = { siteId, courseSetId, orgId, academicYearId }
    const response = await RosterBackendService.getCourseScheduleInfoForSite(request);
    if (response.isOk()) {
        return response.value;
    }

    return false;
}

async function reprovisionClass(siteId: number, orgId: string, classId: string, academicYearId: string) {
    if (!siteId || !orgId || !classId || !academicYearId) {
        return null;
    }
    const response = await RosterBackendService.reprovisionClass({ siteId, orgId, classId, academicYearId });
    if (response.isOk()) {
        return true;
    }
    return false;
}

async function reprovisionAssignments(siteId: number, user: UserAccountDTO, academicYearId: string, role: string | null) {
    if (!siteId || !user.userId || !user.id || !user.primaryLanguage || !academicYearId) {
        return null;
    }

    if (role === UserRole.administrator) {
       return reprovisionAdmin(
          siteId,
          user.userId,
          user.id,
          academicYearId,
          user.primaryLanguage
        );
    } else {
        return reprovisionUser(
            siteId,
            user.userId,
            user.id,
            academicYearId,
            user.primaryLanguage,
            user.grade
        );
    }
  
}

async function reprovisionUser(siteId: number, userId: string, userAccountId: string, academicYearId: string, primaryLanguage: string, grade?: string) {
    const response = await RosterBackendService.reprovisionUser({ 
        siteId, 
        rosterUserId: userId, 
        userAccountId, 
        primaryLanguage, 
        academicYearId,
        grade
    });

    return (response.isOk() ? true : false);
}

async function reprovisionAdmin(siteId: number, userId: string, userAccountId: string, academicYearId: string, primaryLanguage: string) {
    const response = await RosterBackendService.reprovisionAdmin({ 
        siteId, 
        rosterUserId: userId, 
        userAccountId, 
        primaryLanguage, 
        academicYearId,
    });

    return (response.isOk() ? true : false);
}

async function getUserScheduleData(siteId: number, userId: string, userAccountId: string, orgId: string, academicYearId: string, languageCode: string) {
    const request: UserScheduleRequest = { siteId, orgId, academicYearId, userId, userAccountId, languageCode }
    const response = await RosterBackendService.getUserSchedule(request);
    if (response.isOk()) {
        return response.value;
    }

    return undefined;
}

async function getUserLessonHistoryData(siteId: number, userAccountId: string, academicYearId: string, schoolYear: number, languageCode: Languages, timezone: string, startDate: Date, endDate: Date, offset: number, limit: number ) {
    const requestDates: ReportDates = {
        academicYearId,
        startDate: startDate,
        endDate: endDate,
        schoolYear: schoolYear,
        localStartDate: format(toZonedTime(startDate, timezone), "yyyy-MM-dd'T'HH:mm:ss", { timeZone: timezone }),
        localEndDate: format(toZonedTime(endDate, timezone), "yyyy-MM-dd'T'HH:mm:ss", { timeZone: timezone }),
        timezone: timezone,
    }

    const requestFilters: LessonHistoryFilterTypes = {
        studentUserAccountId: userAccountId,
        lessonStatus: [LessonStatusFilter.Progress, LessonStatusFilter.Complete],
        reportDates: requestDates,
        languageCode: languageCode,
    }

    const request: LessonHistoryDataRequest = {
        siteId,
        userAccountId,
        filters: requestFilters,
        offset: offset,
        limit: limit,
    }

    const response = await RosterBackendService.getLessonHistoryReportData(request);
    if (response.isOk()) {
        return response.value;
    }

    return undefined;
}

async function getActivityQuestionScores(activityAttemptId: string) {
    const response = await RosterBackendService.getActivityQuestionScores({ activityAttemptId });
    if (response.isOk()) {
        return response.value;
    }

    return undefined;
}

async function modifyActivityQuestionScores(
  activityAttemptId: string,
  activityQuestions: ActivityQuestionScore[],
  courseType: BeableCourseType,
) {
  const request: ModifyActivityQuestionScoresRequest = {
    activityAttemptId,
    activityQuestions,
    courseType,
  };
  const updateResponse =
    await RosterBackendService.modifyActivityQuestionScores(request);
    return updateResponse.isOk() ? updateResponse.value : undefined;
}

export default {
    getContentCourses, getCourseSets, getCourseSet, saveCourseSet, getCourseSetStatusSummaryForSite, provisionSiteCourse,
    deprovisionSiteCourse, getCourseAccessRules, getCourseDeliveryRules, getProvisionedContentCourses, cloneSchedule,
    getCourseScheduleData, reprovisionClass, reprovisionUser, reprovisionAdmin, reprovisionAssignments, getUserScheduleData, 
    getCoursesForLocation, getUserLessonHistoryData, getActivityQuestionScores, modifyActivityQuestionScores
}
